import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { generateUniqueId } from '../../utils/helpers';
import { useNavigate, useParams } from 'react-router-dom';
import AiFlashcardGenerator from '../../components/ui/AiFlashcardGenerator';
import ConfirmActionModal from '../../components/ui/ConfirmActionModal';
import { useCategoriesQuery } from '../../hooks/queries/useCategoriesQuery';
import { useCreateCategoryMutation } from '../../hooks/mutations/useCreateCategoryMutation';
import { useCreateDeckMutation } from '../../hooks/mutations/useCreateDeckMutation';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { hasRichTextContent } from '../../utils/hasRichTextContent';
import RichTextContent from '../../components/ui/RichTextContent';

import type { CategoryDto, CreateDeckResponse, GeneratedFlashCardDto } from '../../types';
import { getErrorMessage } from '../../utils/getErrorMessage';

interface DraftCategory {
  id: string;
  name: string;
}

interface DraftFlashcard {
  id: string;
  question: string;
  answer: string;
}

interface CreateDeckProps {
  onSave?: (deck: CreateDeckResponse) => void;
  categories?: CategoryDto[];
  onCreateCategory?: (category: CategoryDto) => void;
}

/**
 * Create deck component
 * @param {*} param0
 * @returns
 */
const CreateDeck = ({
  onSave = () => {},
  categories: initialCategories = [],
  onCreateCategory = () => {},
}: CreateDeckProps) => {
  const { categoryId } = useParams();
  
  const DRAFT_CATEGORY_PREFIX = 'draft-category-'

  const [deckName, setDeckName] = useState('');
  const [deckDescription, setDeckDescription] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [draftCategories, setDraftCategories] = useState<DraftCategory[]>([]);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  const [flashcards, setFlashcards] = useState<DraftFlashcard[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [flashcardError, setFlashcardError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [flashcardToDelete, setFlashcardToDelete] = useState<string | null>(null);

  const navigate = useNavigate();

  const {
    data: fetchedCategories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    error: categoriesQueryError,
  } = useCategoriesQuery();

  const createCategoryMutation = useCreateCategoryMutation();

  const createDeckMutation = useCreateDeckMutation();

  const persistedCategories = fetchedCategories.length > 0 ? fetchedCategories : initialCategories;

  const categories = useMemo(() => {
    return [...draftCategories, ...persistedCategories]
  }, [draftCategories, persistedCategories])

  const error =
  categoriesQueryError?.message ||
  (categoriesError ? 'Unable to load categories. Please try again.' : '');

  const handleCreateCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;

    const existingPersistedCategory = persistedCategories.find(
      (category) => (category.name ?? '').trim().toLowerCase() === name.toLowerCase()
    );

    if (existingPersistedCategory) {
      setSelectedCategoryId(existingPersistedCategory.id ?? '');
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      setSaveError('')
      return;
    }

    const existingDraftCategory = draftCategories.find(
      (category) => (category.name ?? '').trim().toLowerCase() === name.toLowerCase()
    );

    if (existingDraftCategory) {
      setSelectedCategoryId(existingDraftCategory.id);
      setNewCategoryName('');
      setShowNewCategoryInput(false);
      setSaveError('');
      return;
    }

    const draftCategory = {
      id: `${DRAFT_CATEGORY_PREFIX}${generateUniqueId()}`,
      name,
    };

    setDraftCategories((prev) => [draftCategory, ...prev]);
    setSelectedCategoryId(draftCategory.id);
    setNewCategoryName('');
    setShowNewCategoryInput(false);
    setSaveError('');
  };

  const handleSaveDeck = async () => {
    if (!deckName.trim()) {
      setSaveError('Please provide a deck name before saving.');
      return;
    }

    if (!deckDescription.trim()) {
      setSaveError('Please provide a deck description before saving.');
      return;
    }

    if (!selectedCategoryId) {
      setSaveError('Please provide a category name before saving');
      return;
    }

    const validFlashcards = flashcards.filter(
      (card) =>
        hasRichTextContent(card.question) && hasRichTextContent(card.answer)
    );

    if (validFlashcards.length === 0) {
      setSaveError('Please add at least one flashcard before saving.');
      return;
    }

    let resolvedCategoryId = selectedCategoryId;

    if (selectedCategoryId.startsWith(DRAFT_CATEGORY_PREFIX)){
      const selectedDraftCategory = draftCategories.find(
        (category) => category.id === selectedCategoryId
      );

      if (!selectedDraftCategory) {
        setSaveError('Please select or create a category before saving')
        return;
      }

      try {
        const { category: createdCategory } = await createCategoryMutation.mutateAsync({
          category: { name: selectedDraftCategory.name },
        });
        if (!createdCategory?.id) {
          throw new Error('The created category did not include an ID.');
        }

        resolvedCategoryId = createdCategory.id;
        onCreateCategory(createdCategory)
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to create category. Please try again.');
        setSaveError(message);
        return;
      }
    }

    const payload = {
      name: deckName.trim(),
      description: deckDescription.trim(),
      categoryId: resolvedCategoryId,
      flashCards: validFlashcards.map(({ question, answer }) => ({
        question,
        answer,
      })),
    };

    try {
      const createdDeck = await createDeckMutation.mutateAsync({ deck: payload });
      onSave(createdDeck);
      navigate('/home');
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to save deck. Please try again.');

      setSaveError(message);
    }
  };

  const handleAddFlashcard = () => {
    const questionHtml = currentQuestion;
    const answerHtml = currentAnswer;
    if (!hasRichTextContent(questionHtml) || !hasRichTextContent(answerHtml)) {
      setFlashcardError('Please enter both a question and an answer.');
      return;
    }

    setFlashcards((prev) => [
      ...prev,
      { id: generateUniqueId(), question: questionHtml, answer: answerHtml },
    ]);
    setCurrentQuestion('');
    setCurrentAnswer('');
    setFlashcardError('');
  };

  const handleRemoveFlashcard = (id: string) => {
    setFlashcards((prev) => prev.filter((card) => card.id !== id));
  };

  const handleApproveAiCards = (approvedCards: GeneratedFlashCardDto[]) => {
    setFlashcards((prev) => [
      ...prev,
      ...approvedCards.map((card) => ({
        id: generateUniqueId(),
        question: card.question ?? '',
        answer: card.answer ?? '',
      })),
    ]);
    setFlashcardError('');
    setSaveError('');
  };

  useEffect(() => {
    if (selectedCategoryId || categories.length === 0) return;

    const hasRequestedCategory = categories.some(
      (category) => String(category.id) === String(categoryId)
    );

    setSelectedCategoryId(
      hasRequestedCategory ? categoryId ?? '' : categories[0]?.id || ''
    );
  }, [categories, categoryId, selectedCategoryId]);

  if (categoriesLoading && categories.length === 0) {
    return (
      <div className='flex justify-center items-center py-10'>
        <p className='text-gray-500'>Loading categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='max-w-3xl mx-auto bg-white p-6 rounded shadow'>
        <p className='text-red-600 mb-4'>{error}</p>
        <button
          className='px-4 py-2 bg-indigo-600 text-white rounded'
          onClick={() => navigate('/home')}
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto'>
      <ConfirmActionModal
        isOpen={Boolean(flashcardToDelete)}
        title='Delete this flashcard?'
        message='Are you sure you want to remove this flashcard from the deck draft?'
        confirmText='Delete Flashcard'
        onCancel={() => setFlashcardToDelete(null)}
        onConfirm={() => {
          if (flashcardToDelete) handleRemoveFlashcard(flashcardToDelete);
          setFlashcardToDelete(null);
        }}
      />

      <h1 className='text-2xl font-bold text-gray-800 mb-6'>
        Create New Flashcard Deck
      </h1>

      {/* Deck Info */}
      <div className='bg-white p-6 rounded-lg shadow-md mb-6'>
        <h2 className='text-lg font-semibold mb-4'>Deck Information</h2>

        {/* Category */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Category
          </label>

          {showNewCategoryInput ? (
            <div className='flex space-x-2'>
              <input
                type='text'
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className='flex-grow p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500'
                placeholder='New Category Name'
              />
              <button
                onClick={handleCreateCategory}
                className='px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700'
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowNewCategoryInput(false);
                  setNewCategoryName('');
                }}
                className='px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400'
              >
                Cancel
              </button>
            </div>
          ) : (
            // {categoryError && (
            //     <div className="text-sm text-red-600 mt-2">
            //         {categoryError}
            //     </div>
            // )}
            <div className='flex space-x-2'>
              <select
                value={selectedCategoryId}
                onChange={(e) => {
                  setSelectedCategoryId(e.target.value);
                  if (saveError) setSaveError('');
                }}
                className='flex-grow p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500'
              >
                <option value='' disabled>
                  Select a category
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowNewCategoryInput(true)}
                className='px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center'
              >
                <PlusIcon size={16} className='mr-1' />
                New
              </button>
            </div>
          )}
        </div>

        {/* Deck name */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Deck Name *
          </label>
          <input
            type='text'
            value={deckName}
            onChange={(e) => {
              setDeckName(e.target.value);
              if (saveError) setSaveError('');
            }}
            className='w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500'
            placeholder='e.g., Biology 101'
          />
        </div>

        {/* Description */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Deck Description *
          </label>
          <textarea
            value={deckDescription}
            onChange={(e) => {
              setDeckDescription(e.target.value);
              if (saveError) setSaveError('');
            }}
            className='w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500'
            placeholder='What is this deck about?'
            rows={3}
          />
        </div>
      </div>

      {/* Flashcards Section */}
      <div className='bg-white p-6 rounded-lg shadow-md mb-6'>
        <h2 className='text-lg font-semibold mb-4'>Flashcards</h2>

        <AiFlashcardGenerator
          existingCount={flashcards.length}
          onApprove={handleApproveAiCards}
        />

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Question
          </label>
          <RichTextEditor
            value={currentQuestion}
            onChange={setCurrentQuestion}
            placeholder='Enter question'
          />
        </div>
        <div className='mb-5'>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Answer
          </label>
          <RichTextEditor
            value={currentAnswer}
            onChange={setCurrentAnswer}
            placeholder='Enter answer'
          />
        </div>
        <button
          onClick={handleAddFlashcard}
          className='w-full py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700'
        >
          <PlusIcon size={16} className='mr-2 inline' />
          Add Flashcard
        </button>
        {flashcardError && (
          <p className='text-sm text-red-600 mt-2'>{flashcardError}</p>
        )}

        {flashcards.length > 0 && (
          <div className='mt-6 space-y-4'>
            {flashcards.map((card, index) => (
              <div key={card.id} className='border rounded-md p-4 relative'>
                <div className='flex justify-between items-start'>
                  <div>
                    <p className='text-sm font-semibold text-gray-700'>
                      Question {index + 1}
                    </p>
                    <RichTextContent
                      html={card.question}
                      className="text-gray-800"
                    />
                    <p className='text-sm font-semibold text-gray-700 mt-2'>
                      Answer
                    </p>
                    <RichTextContent
                      html={card.answer}
                      className="text-gray-800"
                    />
                  </div>
                  <button
                    className='text-sm text-red-500 hover:text-red-600'
                    onClick={() => setFlashcardToDelete(card.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Deck */}
      <div className='flex justify-end space-x-4'>
        <button
          className='px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300'
          onClick={() => navigate('/home')}
        >
          Cancel
        </button>
        <button
          onClick={handleSaveDeck}
          disabled={createDeckMutation.isPending || createCategoryMutation.isPending}
          className={`px-6 py-2 rounded-md ${
            createDeckMutation.isPending || createCategoryMutation.isPending
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {createDeckMutation.isPending || createCategoryMutation.isPending
            ? 'Saving...'
            : 'Save Deck'}
        </button>
      </div>
      {saveError && (
        <p className='text-sm text-red-600 text-right mt-2'>{saveError}</p>
      )}
    </div>
  );
};

export default CreateDeck;
